import { useTranslation } from 'react-i18next'
import { AlertTriangle, BarChart2, Eye } from 'lucide-react'
import type { AttributeIntelligence as AttrData } from '@/lib/api/amazon-listing'
import { SectionCard } from '@/components/v2/tools/amazon-listing/shared/SectionCard'
import type { TFunction } from 'i18next'

interface AttributeIntelligenceProps {
  data: AttrData
}

export function AttributeIntelligence({ data }: AttributeIntelligenceProps) {
  const { t } = useTranslation()
  if (!data) return null

  return (
    <SectionCard title={t('amazonListing.report.attributeIntelligence')}>
      <div className="space-y-5">
        {data.missing_required?.length > 0 && <MissingRequired items={data.missing_required} t={t} />}
        {data.competitor_common?.length > 0 && <CompetitorCommon items={data.competitor_common} t={t} />}
        {data.vision_detected?.length > 0 && <VisionDetected items={data.vision_detected} t={t} />}
      </div>
    </SectionCard>
  )
}

type MissingItem = AttrData['missing_required'][number]
type CompItem = AttrData['competitor_common'][number]
type VisionItem = AttrData['vision_detected'][number]

function MissingRequired({ items, t }: { items: MissingItem[]; t: TFunction }) {
  return (
    <div>
      <h4 className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2 flex items-center gap-1.5">
        <AlertTriangle className="w-4 h-4 text-red-500" />
        {t('amazonListing.report.attrMissingRequired')}
      </h4>
      <div className="space-y-1.5">
        {items.map((item) => (
          <div key={item.code} className="flex items-start gap-2 bg-red-50/50 dark:bg-red-900/10 rounded-lg px-3 py-2">
            <span className="text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 px-1.5 py-0.5 rounded">{item.label}</span>
            <div className="flex-1 min-w-0 text-xs text-neutral-600 dark:text-neutral-400">
              <span className="text-neutral-500">{t('amazonListing.report.attrSource')}:</span> {item.source}
              {item.suggestion && <p className="mt-0.5"><span className="text-neutral-500">{t('amazonListing.report.attrSuggestion')}:</span> {item.suggestion}</p>}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function CompetitorCommon({ items, t }: { items: CompItem[]; t: TFunction }) {
  return (
    <div>
      <h4 className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2 flex items-center gap-1.5">
        <BarChart2 className="w-4 h-4 text-orange-500" />
        {t('amazonListing.report.attrCompetitorCommon')}
      </h4>
      <div className="space-y-1.5">
        {items.map((item) => (
          <div key={item.code} className="flex items-center gap-3 px-3 py-2 bg-neutral-50 dark:bg-neutral-700/30 rounded-lg">
            <span className="text-xs sm:text-sm font-medium text-neutral-800 dark:text-neutral-200 w-20 sm:w-32 shrink-0 break-words">{item.label}</span>
            <div className="flex-1 h-2 bg-neutral-200 dark:bg-neutral-600 rounded-full overflow-hidden">
              <div className="h-full bg-orange-500 rounded-full" style={{ width: `${Math.round(item.frequency * 100)}%` }} />
            </div>
            <span className="text-xs text-neutral-500 dark:text-neutral-400 w-10 text-right">{Math.round(item.frequency * 100)}%</span>
          </div>
        ))}
      </div>
    </div>
  )
}

function confidenceColor(c: number) {
  if (c >= 0.8) return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
  if (c >= 0.5) return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
  return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
}

function VisionDetected({ items, t }: { items: VisionItem[]; t: TFunction }) {
  return (
    <div>
      <h4 className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2 flex items-center gap-1.5">
        <Eye className="w-4 h-4 text-primary-500" />
        {t('amazonListing.report.attrVisionDetected')}
      </h4>
      <div className="space-y-1.5">
        {items.map((item) => (
          <div key={item.code} className="flex items-center gap-2 px-3 py-2 bg-neutral-50 dark:bg-neutral-700/30 rounded-lg">
            <span className="text-sm text-neutral-800 dark:text-neutral-200 flex-1">{item.value}</span>
            <span className={`text-xs px-1.5 py-0.5 rounded ${confidenceColor(item.confidence)}`}>
              {t('amazonListing.report.attrConfidence')}: {Math.round(item.confidence * 100)}%
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}
